/**
 * F-028 — Responsive layout.
 *
 * Two responsibilities:
 *   1. Renderer + camera resize on window resize / DPR change.
 *   2. Side-panel collapse on narrow viewports (< 768px).
 *
 * Splits cleanly so callers can opt into either piece. The side-panel
 * toggle just adds/removes a `data-collapsed` attribute that Tailwind
 * (or any consumer) keys off of for visibility.
 *
 * F-094 — `window.innerHeight` 는 모바일/태블릿에서 layout viewport 의
 * 최댓값을 돌려줘서 브라우저 chrome (URL 바, 탭) 이 보일 때도 큰 값을
 * 반환 → 캔버스가 보이지 않는 영역까지 그려져 잘려 보임. `visualViewport`
 * API 가 있으면 그걸 우선 사용해 실제 보이는 영역만큼만 렌더.
 */

import type { PerspectiveCamera, WebGLRenderer } from "three";

const NARROW_VIEWPORT_PX = 768;

export interface ResponsiveBindings {
  detach(): void;
  /** Force a manual resize tick (useful after layout changes). */
  refresh(): void;
}

export interface ResponsiveOptions {
  camera: PerspectiveCamera;
  renderer: WebGLRenderer;
  /** Optional: panels to collapse below NARROW_VIEWPORT_PX. */
  collapsiblePanels?: HTMLElement[];
  /** Test seam — supply window dimensions explicitly. */
  viewport?: () => { width: number; height: number };
}

function defaultViewport(): { width: number; height: number } {
  if (typeof window === "undefined") return { width: 0, height: 0 };
  const vv = window.visualViewport;
  if (vv) {
    return { width: vv.width, height: vv.height };
  }
  return { width: window.innerWidth, height: window.innerHeight };
}

export function attachResponsive(opts: ResponsiveOptions): ResponsiveBindings {
  const viewport = opts.viewport ?? defaultViewport;

  const apply = (): void => {
    const { width, height } = viewport();
    if (width <= 0 || height <= 0) return;
    opts.camera.aspect = width / height;
    opts.camera.updateProjectionMatrix();
    opts.renderer.setSize(width, height, false);
    if (opts.collapsiblePanels) {
      const collapsed = width < NARROW_VIEWPORT_PX;
      for (const panel of opts.collapsiblePanels) {
        panel.dataset["collapsed"] = collapsed ? "true" : "false";
      }
    }
  };

  apply();
  if (typeof window !== "undefined") {
    window.addEventListener("resize", apply);
    // visualViewport.resize 는 브라우저 chrome 이 펼쳐지거나 접힐 때, 핀치줌
    // 등 layout viewport 변경 없는 경우에도 발화. iPad 에서 탭 바 토글에
    // 즉시 반응시키는 핵심 이벤트.
    window.visualViewport?.addEventListener("resize", apply);
    window.addEventListener("orientationchange", apply);
  }

  return {
    refresh: apply,
    detach: () => {
      if (typeof window !== "undefined") {
        window.removeEventListener("resize", apply);
        window.visualViewport?.removeEventListener("resize", apply);
        window.removeEventListener("orientationchange", apply);
      }
    },
  };
}

export const __testing = { NARROW_VIEWPORT_PX };
